import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import AddNoticeForm, { NoticeFormData } from "./AddNotice";
import { createNotice, getNotices, updateNotice } from "@/services/notices";
import MasterModal from "../masters/MasterModal";
import { useScreenPermission } from "@/hooks/useScreenPermission";

export default function Notices() {
  const [notices, setNotices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { canCreate ,canUpdate } = useScreenPermission("notices");

  const [editingNotice, setEditingNotice] =
    useState<any>(null);

  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const methods = useForm<NoticeFormData>({
    defaultValues: {
      Message: "",
      Visible: [],
    },
  });

  const loadNotices = async () => {
    try {
      setLoading(true);

      const res = await getNotices();
        const response = res.data as any
      if (response) {
        setNotices(response);
      }
    } catch {
      toast.error("Failed to load notices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotices();
  }, []);

  const openAddModal = () => {
    methods.reset({
      Message: "",
      Visible: [],
    });

    setEditingNotice(null);
    setIsModalOpen(true);
  };

  const openEditModal = (notice: any) => {
    methods.reset({
      Message: notice.Message,
      Visible: notice.Visible,
    });

    setEditingNotice(notice);
    setIsModalOpen(true);
  };

  const handleSubmit = async (
    data: NoticeFormData
  ) => {
    try {
      setLoading(true);

      const response = editingNotice
        ? await updateNotice(editingNotice.id, data)
        : await createNotice(data);

      if (
        response.status === 200 ||
        response.status === 201
      ) {
        toast.success(
          editingNotice
            ? "Notice updated successfully"
            : "Notice created successfully"
        );

        setIsModalOpen(false);
        loadNotices();
        return;
      }

      if (response.errors) {
        Object.entries(response.errors).forEach(
          ([field, value]) => {
            methods.setError(field as any, {
              type: "server",
              message: Array.isArray(value)
                ? value[0]
                : String(value),
            });
          }
        );
      }
    } catch {
      toast.error("Failed to save notice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div
        className="rounded-xl p-6"
        style={{
          background:
            "linear-gradient(135deg,#2d3440 0%,#535c6b 100%)",
        }}
      >
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-white text-2xl font-bold">
              Notices Management
            </h2>

            <p className="text-white/80">
              Create and manage notices
            </p>
          </div>

          {canCreate && <button
            onClick={openAddModal}
            className="bg-white/20 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={18} />
            Add Notice
          </button>}
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {notices.map((notice) => (
            <div
              key={notice.id}
              className="bg-white rounded-xl shadow border p-5"
            >
              <div className="flex justify-between">
                <span
                  className={`px-2 py-1 rounded text-xs ${
                    notice.Status
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {notice.Status
                    ? "Active"
                    : "Inactive"}
                </span>
               {canUpdate && <button
                  onClick={() =>
                    openEditModal(notice)
                  }
                >
                  <Pencil
                    size={18}
                    className="text-blue-600"
                  />
                </button>}
              </div>

              <p className="mt-4 text-gray-700">
                {notice.Message}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {notice.Visible?.map(
                  (role: string) => (
                    <span
                      key={role}
                      className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs"
                    >
                      {role}
                    </span>
                  )
                )}
              </div>

              <p className="text-xs text-gray-500 mt-4">
                {new Date(
                  notice.Created_At
                ).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}

      <MasterModal
        isOpen={isModalOpen}
        title={
          editingNotice
            ? "Edit Notice"
            : "Add Notice"
        }
        onClose={() => setIsModalOpen(false)}
        onSubmit={methods.handleSubmit(
          handleSubmit
        )}
        isLoading={loading}
      >
        <FormProvider {...methods}>
          <AddNoticeForm />
        </FormProvider>
      </MasterModal>
    </div>
  );
}